import * as yup from "yup";

import { licenseNumberSchema } from "./commonSchemas";

export const createEmailSchema = (fieldLabel: string = "email address") =>
  yup
    .string()
    .required("This field is required.")
    .email(`Please enter a valid ${fieldLabel}.`)
    .matches(
      /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/,
      `Please enter a valid ${fieldLabel}.`,
    )
    .max(255, `Email must be 255 characters or less`);

// Password validation schemas
export const passwordSchema = yup
  .string()
  .required("This field is required")
  .max(255, "Password must be at most 255 characters");

export const strongPasswordSchema = yup
  .string()
  .required("Password is required")
  .matches(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,20}$/,
    "Password must be 12-20 characters long, contain at least one uppercase letter, one lowercase letter, one number, and one special character",
  );

// ASCII printable characters only (space to ~)
const asciiPrintable = /^[\x20-\x7E]+$/;

export const updatePasswordSchema = yup.object().shape({
  currentPassword: yup
    .string()
    .required("This field is required")
    .max(20, "Password must be at most 20 characters")
    .matches(asciiPrintable, "Only ASCII printable characters are allowed"),
  newPassword: strongPasswordSchema,
  confirmNewPassword: yup
    .string()
    .required("Confirm password is required")
    .oneOf([yup.ref("newPassword")], "The passwords must match."),
});

// Name validation schemas
export const firstNameSchema = yup.string().required("This field is required");

export const lastNameSchema = yup.string().required("This field is required");

// Phone number validation schema
export const phoneNumberSchema = yup
  .string()
  .required("This field is required")
  .matches(/^[0-9]+$/, "Phone number must contain only numeric characters")
  .max(15, "Phone number must not exceed 15 digits");

// Practice ID validation schema
export const practiceIdSchema = yup
  .string()
  .min(3, "Practice ID must be at least 3 characters")
  .required("Practice ID is required");

// Specialties validation schema
export const specialtiesSchema = yup
  .array()
  .of(yup.string())
  .min(1, "Please select at least one specialty")
  .required("Specialties are required");

// Authentication form schemas
export const signInSchema = yup.object().shape({
  email: createEmailSchema("Practice Email"),
  password: passwordSchema,
});

export const signUpSchema = yup
  .object()
  .shape({ email: createEmailSchema(), password: strongPasswordSchema });

export const resetPasswordSchema = yup.object().shape({
  newPassword: strongPasswordSchema,
  confirmNewPassword: yup
    .string()
    .required("Confirm password is required")
    .oneOf([yup.ref("newPassword")], "The passwords must match."),
});

// Onboarding form schemas
export const providerOnboardingStep1Schema = yup.object().shape({
  licenseNumber: licenseNumberSchema,
  specialties: specialtiesSchema,
});

export const providerOnboardingStep2Schema = yup
  .object()
  .shape({ practiceId: practiceIdSchema });

export const patientOnboardingSchema = yup.object().shape({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  phoneNumber: phoneNumberSchema,
});

export const forgotPasswordSchema = yup
  .object()
  .shape({ email: createEmailSchema() });
// Practice details validation schema
export const practiceDetailsSchema = yup.object().shape({
  practiceName: yup
    .string()
    .max(255, "Practice name must be 255 characters or less")
    .required("This field is required."),
  practiceEmail: createEmailSchema(),
  address: yup.string().required("This field is required."),
  street: yup.string().required("This field is required."),
  city: yup.string().required("This field is required."),
  state: yup.string().required("This field is required."),
  zip: yup
    .string()
    .required("This field is required.")
    .matches(/^[0-9]+$/, "Zip must contain only numbers")
    .length(5, "Zip must be exactly 5 digits"),
  primaryPhoneNumber: phoneNumberSchema,
  faxNumber: yup
    .string()
    .optional()
    .matches(/^\d*$/, "Fax must contain only numbers")
    .max(10, "Fax must be at most 10 digits"),
});

// Provider information validation schema
export const providerInformationSchema = yup.object().shape({
  email: createEmailSchema(),
  firstName: firstNameSchema,
  middleName: yup.string().optional(),
  lastName: lastNameSchema,
  npi: yup
    .string()
    .required("National Provider Identifier is required")
    .matches(/^\d+$/, "NPI must contain only numbers")
    .max(10, "NPI must be at most 10 digits"),
  password: strongPasswordSchema,
  confirmPassword: yup
    .string()
    .required("Confirm password is required")
    .oneOf([yup.ref("password")], "The passwords must match."),
});

// Complete SignUp form validation schema
export const signUpFormSchema = practiceDetailsSchema.concat(
  providerInformationSchema,
);
